'use client';

import axios, { AxiosError } from 'axios';
import { isServer, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { IHousingListResponse, SearchListFilters } from '@/types';
import { GET_HOUSING_LIST_API_ENDPOINT } from '@/constants';
import {
  buildObjectSearchStartPayload,
  getObjectSearchStartSignature,
  pushObjectSearchStart,
  type ObjectSearchSource,
} from '@/lib/analytics';
import { useEffect, useRef } from 'react';

export type UseGetHousingListOptions = Partial<
  UseQueryOptions<IHousingListResponse, AxiosError>
> & {
  trackAsSearch?: boolean;
  searchStartCategoryTitles?: string[];
  searchSource?: ObjectSearchSource;
};

function useTrackObjectSearchStart(
  trackAsSearch: boolean,
  searchObj: SearchListFilters | null | undefined,
  queryEnabled: boolean,
  categoryTitles: string[],
  searchSource: ObjectSearchSource,
) {
  const lastSigRef = useRef<string | null>(null);
  const objectCategory = categoryTitles.filter(Boolean).join(', ');

  useEffect(() => {
    if (!trackAsSearch || !queryEnabled || !searchObj) return;
    const sig = getObjectSearchStartSignature(searchObj);
    if (sig === null || sig === lastSigRef.current) return;
    lastSigRef.current = sig;
    pushObjectSearchStart(buildObjectSearchStartPayload(searchObj, objectCategory, searchSource));
  }, [trackAsSearch, queryEnabled, searchObj, objectCategory, searchSource]);
}

export const useGetHousingList = (
  searchObj?: SearchListFilters | null,
  options?: UseGetHousingListOptions,
) => {
  const {
    trackAsSearch = false,
    searchStartCategoryTitles = [],
    searchSource = 'list',
    ...queryOptions
  } = options ?? {};

  useTrackObjectSearchStart(
    trackAsSearch,
    searchObj,
    queryOptions.enabled !== false,
    searchStartCategoryTitles,
    searchSource,
  );

  return useQuery<IHousingListResponse, AxiosError>({
    queryKey: options?.queryKey ?? ['housing-list', searchObj],
    queryFn: ({ signal }) =>
      axios.post(GET_HOUSING_LIST_API_ENDPOINT, searchObj, { signal }).then((res) => res.data),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    ...queryOptions,
    // A finite gcTime on the server schedules a timer per SSR render that keeps the whole request alive.
    gcTime: isServer ? Infinity : queryOptions.gcTime,
  });
};
